import { ArrowLeftOutlined, MessageOutlined } from '@ant-design/icons'
import assistantStore from '@stores/assistantStore'
import { Alert } from 'antd'
import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'

import { useAssistantSession } from './AssistantSessionContext'
import ConversationRows from './ConversationRows'
import { ERROR_MESSAGES } from './errorMessages'
import { KW } from './ui'

/** How long after the last keystroke the history is filtered. */
const FILTER_DELAY_MS = 300

/**
 * Conversation history: the user's conversations a page at a time ("Load more"), filtered by
 * title on the server. The rows (open, rename, delete) are ConversationRows'.
 */
const ConversationList = () => {
  const session = useAssistantSession()
  const {
    conversations,
    conversationsTotal,
    conversationsHasMore,
    loadingMoreConversations,
    conversationFilter,
    conversationsFiltering,
  } = assistantStore
  const { errorCode } = session
  // What is typed; the list follows a moment later (see FILTER_DELAY_MS).
  const [filterText, setFilterText] = useState(conversationFilter)
  // Once the filter box was used it stays on screen, whatever the list holds: a user who clears
  // it must not have the box they are typing in taken away (and the focus with it).
  const [filterUsed, setFilterUsed] = useState(conversationFilter.trim() !== '')

  useEffect(() => {
    if (filterText.trim() === assistantStore.conversationFilter.trim()) {
      return undefined
    }
    const timer = setTimeout(
      () => assistantStore.filterConversations(filterText),
      FILTER_DELAY_MS
    )
    return () => clearTimeout(timer)
  }, [filterText])

  const filtering = conversationFilter.trim() !== ''
  // The filter the list is waiting for: typed but not sent yet, or sent and not answered yet.
  const pending =
    conversationsFiltering || filterText.trim() !== conversationFilter.trim()

  const changeFilter = (text: string) => {
    setFilterUsed(true)
    setFilterText(text)
  }

  const backToChat = (
    <button
      type="button"
      className="kw-history-back"
      onClick={session.toggleHistory}
    >
      <ArrowLeftOutlined /> Back to chat
    </button>
  )

  // The history of a user who has none - not of one whose filter matches nothing, or who has
  // just cleared the filter: they keep the box they typed in.
  if (!conversations.length && !filterUsed && !pending) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
        <MessageOutlined style={{ fontSize: 28, color: KW.line2 }} />
        <div className="text-sm font-bold" style={{ color: KW.ink }}>
          No conversations yet
        </div>
        <div className="text-[13px]" style={{ color: KW.muted }}>
          Your chats with the Assistant will appear here.
        </div>
        {backToChat}
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className="flex flex-shrink-0 items-center justify-between px-4 py-[10px]"
        style={{ borderBottom: `1px solid ${KW.line}` }}
      >
        <span className="text-[13px] font-extrabold" style={{ color: KW.ink }}>
          Conversations{' '}
          <span style={{ color: KW.muted }}>
            ({Math.max(conversationsTotal, conversations.length)})
          </span>
        </span>
        {backToChat}
      </div>

      <div className="flex-shrink-0 px-3 pt-3">
        <input
          type="search"
          className="kw-history-filter"
          value={filterText}
          maxLength={100}
          placeholder="Filter by title"
          aria-label="Filter conversations by title"
          onChange={e => changeFilter(e.target.value)}
        />
      </div>

      {/* The chat (where errors normally show) is hidden behind the history. */}
      {errorCode === 'CONVERSATION_LOAD_FAILED' && (
        <Alert
          className="mx-3 mt-3 flex-shrink-0"
          type="warning"
          showIcon
          closable
          onClose={session.dismissError}
          message={ERROR_MESSAGES.CONVERSATION_LOAD_FAILED}
        />
      )}

      <div
        className="kw-scroll kw-history min-h-0 flex-1 overflow-y-auto px-2 pb-3"
        aria-busy={pending || undefined}
      >
        <ConversationRows items={conversations} />

        {/* Only once the server has answered for the filter in the box: said (and announced)
          while the answer is on its way, it would be said about a list nobody has seen. */}
        {!conversations.length && !pending && (
          <div
            role="status"
            className="px-3 py-6 text-center text-[13px]"
            style={{ color: KW.muted }}
          >
            {filtering ? (
              <>
                No conversation title contains &ldquo;
                {conversationFilter.trim()}
                &rdquo;.
              </>
            ) : (
              'No conversations yet.'
            )}
          </div>
        )}

        {conversationsHasMore && conversations.length > 0 && (
          <div className="flex justify-center px-3 pt-3">
            <button
              type="button"
              className="kw-history-btn"
              disabled={loadingMoreConversations}
              onClick={assistantStore.loadMoreConversations}
            >
              {loadingMoreConversations ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default observer(ConversationList)
